import React, { useEffect, useMemo, useRef, useState } from "react";
import { Divider, Empty, Form, Modal, Col, Button, Row, Spin, Flex, Switch } from "antd";
import useFormInstance from "antd/es/form/hooks/useFormInstance";
import { useForm } from "antd/lib/form/Form";

import { useGerarRelatorioDeTitulosAcumulados } from "@/Services/Financeiro/Titulo/Relatorios/RelatoriosService";
import TituloSituacaoSelect from "@/Components/Populados/Financeiro/Titulo/TituloSituacao/TituloSituacaoSelect";
import ResponsiveAntdTable from "@/Components/ResponsiveAntdTable";
import PessoaSelectComTipo from "@/Components/Populados/Pessoa/PessoaSelect";
import Formatacoes from "@/Utils/Formatacao";
import Titulo, { EnumTipoConsultaData } from "@/Models/Financeiro/Titulo/Titulo";
import { EnumPessoaTipo, extractorNomePessoa } from "@/Models/Pessoa/Pessoa";
import { EnumTituloSituacao } from "@/Models/Financeiro/Titulo/TituloSituacao";
import { Parametros as ParametrosDosTitulosAcumulados } from "@/Models/Financeiro/Titulo/Relatorios/RelatorioDeTitulosAcumulados";
import TipoConsultaDataSelect from "../../TipoConsultaData/TipoConsultaDataSelect";

import dayjs from "dayjs"
import { Balanco } from "@/Components/Balanco";
import ComponentePrint, { IComponentePrintRef } from "@/Components/Base/Baixar/ComponentePrint";
import { useListar } from "@/Services/Financeiro/Titulo/TituloSituacaoService";
import InputRangePicker from "@/Components/Base/Input/InputRangePicker";
import OpceosTitulo from "../../OpceosTitulo";
import { SettingOutlined } from "@ant-design/icons";

type IFiltro = Omit<ParametrosDosTitulosAcumulados, "de" | "ate"> & {
    intervalo?: [ Date, Date ],
    tipoDaPessoa: EnumPessoaTipo,
    receita: boolean
}

const Filtro: React.FC<{}> = () => {
    const form = useFormInstance<IFiltro>()

    if(form == undefined) throw new Error("O componente deve estar aninhado em um formulário");

    return (
        <React.Fragment>
            <Row gutter={[8, 8]}>
                <Col xs={"auto"}>
                    <Form.Item<IFiltro> name={["receita"]} initialValue={false} label={"Receita?"} rules={[{ required : true}]}>
                        <Switch checkedChildren="Sim" unCheckedChildren="Não" />
                    </Form.Item>
                </Col>

                <Col xs={18} sm={12} md={8} lg={5} xl={4} xxl={3}>
                    <Form.Item label="Tipo de consulta" initialValue={EnumTipoConsultaData.DataVencimento} rules={[{ required : true, message: "Selecione o tipo de consulta" }]} name={["tipoDeConsulta"]}>
                        <TipoConsultaDataSelect />
                    </Form.Item>
                </Col>

                <Col xs={24} lg={5}>
                    <Form.Item<IFiltro>
                        label="Intervalo"
                        name={["intervalo"]}
                        rules={[
                            {
                                required: true,
                                message: "Selecione o intervalo da busca"
                            },
                        ]}
                        initialValue={[
                            dayjs().startOf('month'),
                            dayjs()
                        ]}
                    >
                        <InputRangePicker
                            allowClear
                            disabledDate={(dia, v) => {
                                if (v.from) {
                                    if (dia < v.from) return true;
                                    if (dia > v.from.add(1, "year")) return true;
                                }
                                return false;
                            }}
                        />
                    </Form.Item>
                </Col>

                <Col xs={18} sm={12} md={8} lg={5} xl={4} xxl={3}>
                    <Form.Item<IFiltro>
                        label="Situação"
                        name={"situacao"}
                    >
                        <TituloSituacaoSelect />
                    </Form.Item>
                </Col>
                
                <Col flex="auto">
                    <PessoaSelectComTipo<IFiltro>
                        names={{
                            tipo: ["tipoDaPessoa"],
                            "*": ["codigoDaPessoa"],
                        }}
                        rules={{
                            tipo: [
                                {required: true, message: "Selecione o tipo da pessoa"}
                            ],
                            "*": [
                                {required: true, message: "Selecione a pessoa"}
                            ],
                        }}
                    />
                </Col>
            </Row>
        </React.Fragment>
    );
}

export const RelatorioDeTitulosAcumulados: React.FC<{}> = () => {

    const refPrint = useRef<IComponentePrintRef|null>(null);
    const [ formularioDeFiltro ] = useForm<IFiltro>();
    const [filtro, setFiltro] = useState<ParametrosDosTitulosAcumulados>();

    const relatorioQuery = useGerarRelatorioDeTitulosAcumulados({enabled: false}, filtro);
    const [statusDoModalDeDetalhes, setStatusDoModalDeDetalhes] = useState<{ exibir: boolean; dia?: Date; }>({exibir: false});
    const titulos = useMemo(() => {
        const itens = relatorioQuery.data?.data?.resultado?.find(f => f.diario.dia === statusDoModalDeDetalhes.dia)?.diario?.titulos ?? [];

        if (itens.length === 0) {
            setStatusDoModalDeDetalhes({ exibir: false });
        }

        return itens;
    }, [relatorioQuery.data?.data, statusDoModalDeDetalhes.dia]);
    
    const onFinish = (novoFiltro: IFiltro)  => {
        setFiltro({
            tipoDeConsulta: novoFiltro.tipoDeConsulta,
            codigoDaPessoa: novoFiltro.codigoDaPessoa!,

            situacao: novoFiltro.situacao,
            de: novoFiltro.intervalo?.[0]!,
            ate: novoFiltro.intervalo?.[1]!,

            receita : novoFiltro.receita ?? false
        })
    }

    useEffect(() => {
        if(filtro != undefined)
            relatorioQuery.refetch();
    }, [filtro])

    const { data: situacoesPossiveis } = useListar({});

    return (
        <div>
            <Modal
                title={"Titulos"}
                open={statusDoModalDeDetalhes.exibir == true}
                width={"80vw"}
                onCancel={() => {
                    setStatusDoModalDeDetalhes({ exibir: false });
                }}
                onOk={() => {}}
                footer={null}
            >
                <ResponsiveAntdTable<Titulo>
                    dataSource={titulos}
                    addTitleOn="md"
                    bordered
                    rowKey={(k) => k.codigo!}
                    disableResponsiveness={true}
                    tableLayout="auto"
                    scroll={{
                        x: true,
                    }}
                    showHeader={true}
                    minColWidthIfResponsivenessDisabled={"120px"}
                    locale={{ emptyText: "Nenhum titulo encontrado." }}
                    columns={[
                        {
                            title: "Beneficiário",
                            dataIndex: ["beneficiario", "nome"],
                            key: "beneficiario",
                            render: (_, titulo) => extractorNomePessoa(titulo.beneficiario),
                            breakpoints: { xs: 12, lg: 4 },
                        },
                        {
                            title: "Devedor",
                            dataIndex: ["devedor", "nome"],
                            key: "devedor",
                            render: (_, titulo) => extractorNomePessoa(titulo.devedor),
                            breakpoints: { xs: 12, lg: 4 },
                        },
                        {
                            title: "Dt. Vencimento",
                            dataIndex: "dataVencimento",
                            key: "dataVencimento",
                            breakpoints: { xs: 8, lg: 3 },
                            render: (v) => Formatacoes.paraData(v)?.toLocaleDateString(),
                        },
                        {
                            title: "Dt. Pagamento",
                            dataIndex: "dataPagamento",
                            key: "dataPagamento",
                            breakpoints: { xs: 8, lg: 3 },
                            render: (v) => Formatacoes.paraData(v)?.toLocaleDateString(),
                        },
                        {
                            title: "Situação",
                            dataIndex: "codSituacao",
                            key: "situacao",
                            breakpoints: { xs: 8, lg: 4 },
                            render: (v: EnumTituloSituacao) => situacoesPossiveis?.find((x) => x.codigo == v)?.nome ?? ""
                        },
                        {
                            title: "Valor Previsto",
                            dataIndex: "valorPrevisto",
                            key: "valorPrevisto",
                            breakpoints: { xs: 12, lg: 3 },
                            render: Formatacoes.formatarReal,
                        },
                        {
                            title: "Valor Recebido",
                            dataIndex: "valorRecebido",
                            key: "valorRecebido",
                            breakpoints: { xs: 12, lg: 3 },
                            render: Formatacoes.formatarReal,
                        },
                        {
                            key: "acoes",
                            title: "Ações",
                            fixed: "right",
                            minWidth : "10px",
                            render: (_, titulo) => <OpceosTitulo
                                isDesativar={true}
                                isAtivar={true}
                                isPagar={true}
                                isVisualizar={true}
                                onSucess={() => relatorioQuery.refetch()}
                                titulo={titulo}
                                children={<SettingOutlined />}
                            />
                        }
                    ]}
                />
            </Modal>

            <ComponentePrint ref={refPrint}>
                <Form<IFiltro> form={formularioDeFiltro} layout="vertical" validateTrigger="onBlur" onFinish={onFinish}>
                    <Filtro />

                    <Row gutter={8}>
                        <Col>
                            <Button htmlType="submit" className="nao-imprimir" type="primary">
                                Obter relatório
                            </Button>
                        </Col>
                        <Col>
                            <Button className="nao-imprimir" type="dashed" onClick={() => refPrint.current?.imprimir()}>
                                Exportar
                            </Button>
                        </Col>
                    </Row>
                </Form>

                <Divider />

                <Spin spinning={relatorioQuery.isLoading} >
                    <Row gutter={[8, 8]}>
                        <style scoped>
                            {`
                            #relatorio-acumulado th, #relatorio-acumulado td {
                                white-space: nowrap;
                                padding: .5em;
                            }

                        `}
                        </style>

                        <Col xs={24}>
                            {relatorioQuery.data?.data == undefined || relatorioQuery.data.data.resultado.length == 0 ? (
                                relatorioQuery.isSuccess ? (
                                    <Empty description="Não há dados" style={{ width: "100%" }} />
                                ) : undefined
                            ) : (
                                <React.Fragment>
                                    {relatorioQuery.data?.data.resultado.map((x, idx) => {
                                        const data = Formatacoes.paraData(x.diario.dia)?.toLocaleDateString();
                                        return (
                                            <React.Fragment key={data}>
                                                {idx > 0 && <Divider />}

                                                <Col xs={24} style={{ overflowX: "auto" }}>
                                                    <table id="relatorio-acumulado" style={{ width: "-webkit-fill-available", textAlign: "left" }}>
                                                        <tbody>
                                                            <tr>
                                                                <th> {data} </th>
                                                                <th> Titulos </th>
                                                                <th> Valor pago </th>
                                                                <th> Valor previsto </th>
                                                                <th> Valor restante </th>
                                                            </tr>
                                                            <tr>
                                                                <th> Dia </th>
                                                                <td> {x.diario.numeroDeTitulos}</td>
                                                                <td>
                                                                    {" "}
                                                                    <Balanco> {Formatacoes.formatarReal(x.diario.valorPago)} </Balanco>
                                                                </td>
                                                                <td>
                                                                    {" "}
                                                                    <Balanco> {Formatacoes.formatarReal(x.diario.valorPrevisto)} </Balanco>
                                                                </td>
                                                                <td>
                                                                    {" "}
                                                                    <Balanco> {Formatacoes.formatarReal(x.diario.valorPendente)} </Balanco>
                                                                </td>
                                                            </tr>
                                                            <tr>
                                                                <th> Acumulado </th>
                                                                <td> {x.acumulado.numeroDeTitulos} </td>
                                                                <td>
                                                                    {" "}
                                                                    <Balanco> {Formatacoes.formatarReal(x.acumulado.valorPago)} </Balanco>
                                                                </td>
                                                                <td>
                                                                    {" "}
                                                                    <Balanco> {Formatacoes.formatarReal(x.acumulado.valorPrevisto)} </Balanco>
                                                                </td>
                                                                <td>
                                                                    {" "}
                                                                    <Balanco> {Formatacoes.formatarReal(x.acumulado.valorPendente)} </Balanco>
                                                                </td>
                                                            </tr>
                                                        </tbody>
                                                    </table>
                                                </Col>

                                                <Col xs={24} style={{ display: "flex", flexDirection: "row-reverse" }}>
                                                    <Button
                                                        className="nao-imprimir"
                                                        onClick={() => {
                                                            setStatusDoModalDeDetalhes({ exibir: true, dia : x.diario.dia });/// titulos: x.diario.titulos });
                                                        }}
                                                    >
                                                        Detalhes
                                                    </Button>
                                                </Col>
                                            </React.Fragment>
                                        );
                                    })}
                                </React.Fragment>
                            )}
                        </Col>
                    </Row>
                </Spin>
            </ComponentePrint>
        </div>
    );
}
